/** Abrechnungstabelle der Mitnahmeliste (Messe) — reine Darstellung.
 *  Suche/Sortierung wirken nur auf die Anzeige; Summen kommen aus ALLEN Positionen. */
import { eur } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Th, Td, SortTh } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Pencil, Trash2 } from "lucide-react";

export default function MesseSettleTable({ visible, items, reconciled, canEdit, closed, busy,
  entries, onEnter, sortProps, keyOf, valOf, overbooked, soldOf, retOf, schwundOf, revOf,
  shrValOf, dbOf, totals, onEditTake, onRemoveTake }) {
  const { t } = useI18n();
  const editable = canEdit && !closed && !reconciled;
  const sum = (fn) => items.reduce((s, it) => s + fn(it), 0);

  const qtyCell = (it, field, other, testId) => {
    if (reconciled) {
      return <span className="font-mono" data-testid={`${testId}-actual-${it.sku}`}>
        {field === "sold" ? soldOf(it) : retOf(it)}</span>;
    }
    if (!canEdit || closed) return <span className="text-muted-foreground">—</span>;
    return (
      <Input type="number" min="0" max={Math.max(0, (it.on_fair || 0) - valOf(it, other))}
        className="w-20 ml-auto" value={entries[keyOf(it)]?.[field] ?? ""} placeholder="0"
        onChange={(e) => onEnter(it, field, e.target.value)} data-testid={`${testId}-${it.sku}`} />
    );
  };

  return (
    <div className="overflow-auto max-h-[60vh] rounded-md border border-border">
      <table className="w-full"><thead className="bg-muted/50"><tr>
        <SortTh sticky {...sortProps("sku")}>SKU</SortTh>
        <SortTh sticky {...sortProps("name")}>{t("op.item")}</SortTh>
        <SortTh sticky right {...sortProps("taken")}>{t("me.taken")}</SortTh>
        {!reconciled && <SortTh sticky right {...sortProps("on_fair")}>{t("me.onFair")}</SortTh>}
        <SortTh sticky right {...sortProps("sold")}>{t("me.sold")}</SortTh>
        <SortTh sticky right {...sortProps("returned")}>{t("me.returned")}</SortTh>
        <SortTh sticky right {...sortProps("schwund")}>{t("me.shrinkage")}</SortTh>
        {reconciled && <Th sticky right>{t("common.revenueNet")}</Th>}
        {reconciled && <Th sticky right>{t("me.shrinkageValue")}</Th>}
        {reconciled && <Th sticky right>{t("common.margin")}</Th>}
        {editable && <Th sticky></Th>}</tr></thead>
        <tbody data-testid="messe-settle-table">
          {visible.length === 0 && (
            <tr><Td className="text-muted-foreground" data-testid="settle-no-match"
              colSpan={reconciled ? 9 : (canEdit && !closed ? 8 : 7)}>{t("common.noMatch")}</Td></tr>
          )}
          {visible.map((it) => (
            <tr key={keyOf(it)} className={overbooked(it) ? "bg-destructive/5" : ""}>
              <Td mono>{it.sku}</Td><Td>{it.name}</Td>
              <Td right mono data-testid={`taken-${it.sku}`}>{it.taken}</Td>
              {!reconciled && <Td right mono className={it.on_fair > 0 ? "font-semibold" : "text-muted-foreground"}>{it.on_fair}</Td>}
              <Td right>{qtyCell(it, "sold", "returned", "sold")}</Td>
              <Td right>{qtyCell(it, "returned", "sold", "returned")}</Td>
              <Td right mono data-testid={`schwund-${it.sku}`}
                className={schwundOf(it) > 0 ? "text-destructive font-semibold" : "text-muted-foreground"}>{schwundOf(it)}</Td>
              {reconciled && <Td right mono className="whitespace-nowrap" data-testid={`revenue-${it.sku}`}>{eur(revOf(it))}</Td>}
              {reconciled && <Td right mono className="whitespace-nowrap text-destructive" data-testid={`schwund-value-${it.sku}`}>{eur(shrValOf(it))}</Td>}
              {reconciled && <Td right mono className="whitespace-nowrap text-primary" data-testid={`margin-${it.sku}`}>{eur(dbOf(it))}</Td>}
              {editable && (
                <Td right>
                  <div className="flex justify-end gap-1">
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => onEditTake(it)}
                      disabled={busy} title={t("me.changeTakeQty")} data-testid={`take-edit-${it.sku}`}>
                      <Pencil className="h-3.5 w-3.5" /></Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => onRemoveTake(it)}
                      disabled={busy} title={t("me.removeTake")} data-testid={`take-remove-${it.sku}`}>
                      <Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
                  </div>
                </Td>
              )}
            </tr>
          ))}
        </tbody>
        <tfoot><tr className="border-t-2 border-border font-medium bg-muted/30" data-testid="messe-settle-foot">
          <Td>{t("common.sum")}</Td><Td></Td>
          <Td right mono>{sum((it) => it.taken || 0)}</Td>
          {!reconciled && <Td right mono>{sum((it) => it.on_fair)}</Td>}
          <Td right mono>{totals.sold}</Td>
          <Td right mono>{totals.returned}</Td>
          <Td right mono className={totals.schwundQty > 0 ? "text-destructive" : ""}>{totals.schwundQty}</Td>
          {reconciled && <Td right mono className="whitespace-nowrap">{eur(totals.revenue)}</Td>}
          {reconciled && <Td right mono className="whitespace-nowrap text-destructive">{eur(totals.schwundValue)}</Td>}
          {reconciled && <Td right mono className="whitespace-nowrap text-primary">{eur(totals.margin)}</Td>}
          {editable && <Td></Td>}
        </tr></tfoot>
      </table>
    </div>
  );
}
